<template>
  <div>
    <div class="pb3">
      <div class="flex justify-between items-center xs-hide sm-hide">
        <div>
          <p class="h5n m0 bold">{{ getReferencelabel('MAIN_ACCOUNT') }}</p>
        </div>
        <ButtonV2 :color="accountDeletionAllowed ? 'red' : 'grey'" :reference="'DELETE_ACCOUNT'" rounded class="px2 py1" :loading="accountDeletionCheckLoading" @click="$emit('show-delete-account-modal')" />
      </div>
      <!-- Mobile -->
      <div class="flex flex-column center items-center gap2 lg-hide md-hide">
        <div>
          <p class="h5n m0 bold">{{ getReferencelabel('MAIN_ACCOUNT') }}</p>
        </div>
        <ButtonV2 :color="accountDeletionAllowed ? 'red' : 'grey'" :reference="'DELETE_ACCOUNT'" rounded class="col-12 px2 py1" :loading="accountDeletionCheckLoading" @click="$emit('show-delete-account-modal')" />
      </div>
    </div>

    <div class="grid gap3 form-row">
      <FormInput
        id="gender"
        v-model:value="modelValue.gender"
        v-model:errorMessage="errors.gender"
        class="grid-col-4"
        type="radio"
        label="TITLE"
        :options="[
          { value: 'mr', text: 'Monsieur' },
          { value: 'mme', text: 'Madame' }
        ]"
      />

      <FormInput
        id="firstname"
        v-model:value="modelValue.firstname"
        v-model:errorMessage="errors.firstname"
        class="grid-col-4"
        type="text"
        label="FIRST_NAME"
        placeholder="YOUR_FIRST_NAME"
      />

      <FormInput
        id="lastname"
        v-model:value="modelValue.lastname"
        v-model:errorMessage="errors.lastname"
        class="grid-col-4"
        type="text"
        label="LAST_NAME"
        placeholder="YOUR_LAST_NAME"
      />

      <FormInput
        id="nationality"
        v-model:value="modelValue.nationality"
        v-model:errorMessage="errors.nationality"
        class="grid-col-4"
        type="select"
        label="NATIONALITY"
        placeholder="Votre nationalité"
        :options="countryOptions"
      />

      <FormInput
        id="birth_date"
        v-model:value="modelValue.birth_date"
        v-model:errorMessage="errors.birth_date"
        class="grid-col-4"
        type="text"
        label="BIRTH_DATE"
        placeholder="Ex: 27/03/1980"
        :disabled="true"
      />

      <FormInput
        id="email"
        v-model:value="modelValue.email"
        v-model:errorMessage="errors.email"
        class="grid-col-4"
        type="email"
        label="EMAIL"
        :disabled="true"
      />

      <FormInput
        id="mobilephone"
        v-model:value="modelValue.mobilephone"
        v-model:errorMessage="errors.mobilephone"
        class="grid-col-4"
        type="tel"
        label="PHONE_NUMBER"
        placeholder="MOBILE_PLACEHOLDER"
      />

      <FormInput
        id="country"
        v-model:value="modelValue.country"
        v-model:errorMessage="errors.country"
        class="grid-col-3"
        type="select"
        label="COUNTRY"
        placeholder="Votre pays de résidence"
        :options="countryOptions"
      />

      <FormInput
        id="zip_code"
        v-model:value="modelValue.zip_code"
        v-model:errorMessage="errors.zip_code"
        class="grid-col-1"
        type="text"
        label="ZIP_CODE"
      />

      <FormInput
        id="town"
        v-model:value="modelValue.town"
        v-model:errorMessage="errors.town"
        class="grid-col-4"
        type="text"
        label="CITY"
      />

      <FormInput
        id="street_number"
        v-model:value="modelValue.street_number"
        v-model:errorMessage="errors.street_number"
        class="grid-col-4"
        type="text"
        label="ADDRESS_1"
      />

      <FormInput
        id="street_name"
        v-model:value="modelValue.street_name"
        v-model:errorMessage="errors.street_name"
        class="grid-col-4"
        type="text"
        label="ADDRESS_2"
      />

      <FormInput
        id="address2"
        v-model:value="modelValue.address2"
        v-model:errorMessage="errors.address2"
        class="grid-col-4"
        type="text"
        label="ADDRESS_COMPLEMENT"
      />
    </div>

    <div>
      <div class="flex flex-column gap2 pt3">
        <div class="flex justify-between col-12 xs-hide sm-hide gap1">
          <ButtonV2 :disabled="disableFormButtons" color="light" :action="resetForm" :reference="'CANCEL'" rounded class="col-6 px2 py1" />
          <ButtonV2 :disabled="disableFormButtons" color="dark" :action="saveForm" :reference="'SAVE'" rounded class="col-6 px2 py1" />
        </div>
        <!-- Mobile -->
        <div class="flex flex-column justify-between col-12 gap2 md-hide lg-hide">
          <ButtonV2 :disabled="disableFormButtons" color="light" :action="resetForm" :reference="'CANCEL'" rounded class="col-12 px2 py1" />
          <ButtonV2 :disabled="disableFormButtons" color="dark" :action="saveForm" :reference="'SAVE'" rounded class="col-12 px2 py1" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import FormInput from '@/components/inputs/FormInput';
import ButtonV2 from '@/components/elements/ButtonV2';
import { countryOptions, getReferencelabel } from '@/mixins/utils';
import { validateForm } from '@/mixins/formValidation';

export default {
  name: 'ProfileForm',
  components: { FormInput, ButtonV2 },
  props: {
    accountDeletionAllowed: {
      type: [Boolean, null],
      default: null
    },
    accountDeletionCheckLoading: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Object,
      required: true
    }
  },
  emits: ['save-form', 'reset-form', 'show-delete-account-modal'],

  setup (props, { emit }) {
    const store = useStore();

    const errors = ref({});

    const user = computed(() => store.state.user);

    const profileFormValidations = {
      firstname: {
        rules: ['required', 'min:2'],
        regex: /^([A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/,
        errorMessages: {
          required: 'FIRST_NAME_ERROR',
          min: 'FIRST_NAME_MORE_2',
          regex: 'FIRST_NAME_ONLY_LETTERS'
        }
      },
      lastname: {
        rules: ['required', 'min:2'],
        regex: /^([A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/,
        errorMessages: {
          required: 'LASTNAME_ERROR',
          min: 'LASTNAME_MORE_2',
          regex: 'LASTNAME_ONLY_LETTERS'
        }
      },
      nationality: { errorMessages: { required: 'ERROR_REQUIRED' } },
      birth_date: {
        rules: ['required', 'min_age:1,dd/MM/yyyy', 'max_age:100,dd/MM/yyyy'],
        errorMessages: {
          required: 'ERROR_BIRTH_DATE',
          min_age: 'ERROR_BIRTH_DATE_MIN_AGE_1',
          max_age: 'ERROR_BIRTH_DATE_MAX_AGE_100'
        }
      },
      email: {
        rules: ['required', 'email'],
        errorMessages: {
          required: 'ERROR_EMAIL_EMPTY',
          email: 'ERROR_WRONG_EMAIL'
        }
      },
      mobilephone: {
        rules: ['required', 'phone_number'],
        errorMessages: { required: 'ERROR_PHONE_EMPTY' }
      },
      country: {
        rules: ['required'],
        errorMessages: { required: 'ERROR_REQUIRED' }
      },
      zip_code: {
        rules: ['required', 'min:3', 'max:10'],
        regexp: /^([A-Z a-z0-9]*)$/,
        errorMessages: {
          required: 'ERROR_POSTAL_CODE',
          min: 'ERROR_POSTAL_CODE_NUMBERS_3',
          max: 'ERROR_POSTAL_CODE_NUMBERS_10',
          regex: 'ERROR_POSTAL_CODE_NUMBERS'
        },
        customValidation: (value, formData) => {
          if (formData.country === 'FR') {
            const frenchPostalRegex = /^([0-9]){5}$/;
            return !frenchPostalRegex.test(value) ? 'ERROR_POSTAL_CODE_FRANCE' : null;
          }
          return null;
        }
      },
      town: {
        rules: ['required', 'min:2', 'max:30'],
        regex: /^([A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/,
        errorMessages: {
          required: 'ERROR_CITY',
          min: 'ERROR_CITY_MIN_CHARS_2',
          max: 'ERROR_CITY_MAX_CHARS_30',
          regex: 'ERROR_CITY_ONLY_LETTERS_AND_NUMBERS'
        },
        customValidation: (value, formData) => {
          if (formData.country === 'FR') {
            const frenchCityRegex = /^(["A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/;
            return !frenchCityRegex.test(value) ? 'ERROR_WRONG_FORMAT' : null;
          }
          return null;
        }
      },
      street_number: {
        rules: ['required', 'min:2', 'max:30'],
        errorMessages: {
          required: 'ERROR_STREET_NUMBER',
          min: 'ERROR_STREET_NUMBER_MIN_CHARS_2',
          max: 'ERROR_STREET_NUMBER_MAX_CHARS_30',
          regex: 'ERROR_STREET_NUMBER_ONLY_LETTERS_AND_NUMBERS'
        }
      },
      street_name: {
        regex: /^([A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/,
        rules: ['required', 'min:2', 'max:30'],
        errorMessages: {
          required: 'ERROR_STREET_NAME',
          min: 'ERROR_STREET_NAME_MIN_CHARS_2',
          max: 'ERROR_STREET_NAME_MAX_CHARS_30',
          regex: 'ERROR_STREET_NAME_ONLY_LETTERS_AND_NUMBERS'
        }
      },
      address2: {
        rules: ['min:2', 'max:30'],
        regex: /^([A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/,
        errorMessages: {
          min: 'ERROR_MAILING_ADDRESS_MIN_CHARS_2',
          max: 'ERROR_MAILING_ADDRESS_MAX_CHARS_30',
          regex: 'ERROR_ADDRESS_LETTERS_CHARACTERS'
        }
      }
    };

    const disableFormButtons = computed(() => {
      const updatedDataWithoutPhoneFormat = { ...props.modelValue, mobilephone: props.modelValue.mobilephone.replaceAll(' ', '') };
      return JSON.stringify(user.value) === JSON.stringify(updatedDataWithoutPhoneFormat);
    });

    const saveForm = () => {
      if (disableFormButtons.value) {
        return;
      }

      errors.value = validateForm(props.modelValue, profileFormValidations);
      if (Object.keys(errors.value).length) {
        return;
      }

      emit('save-form');
    };

    const resetForm = () => {
      if (disableFormButtons.value) {
        return;
      }

      errors.value = {};
      emit('reset-form');
    };

    return {
      countryOptions,
      getReferencelabel,
      errors,
      saveForm,
      resetForm,
      disableFormButtons,
      user
    };
  }
};
</script>
